import React, { useContext, useEffect, useRef } from 'react';

import AppContext from '../../context';
import {
    trackBannerClick,
    trackBannerImpression,
} from '../../tracking/bannerTracking';
import { UiBannerItem } from '../../types';

import styles from './FlyoutBanner.module.scss';

interface FlyoutBannerProps {
    banner: UiBannerItem | null;
    animalType?: string;
    categoryType?: string;
    isFlyoutOpen?: boolean;
}

export function FlyoutBanner({
    banner,
    animalType,
    categoryType,
    isFlyoutOpen = true,
}: FlyoutBannerProps): React.JSX.Element | null {
    const { siteId, locale } = useContext(AppContext);

    const trackedImpressionsRef = useRef(new Set<string>());
    const bannerRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        const desktopImage = banner?.desktopImage;
        const pathname =
            typeof window !== 'undefined' ? window.location.pathname : '';

        if (!desktopImage || isFlyoutOpen === false) {
            return;
        }

        const impressionKey = [
            desktopImage,
            categoryType ?? '',
            animalType ?? '',
            siteId ?? '',
            locale ?? '',
            pathname,
        ].join('|');

        if (trackedImpressionsRef.current.has(impressionKey)) {
            return;
        }

        const trackImpression = () => {
            if (trackedImpressionsRef.current.has(impressionKey)) {
                return;
            }
            trackedImpressionsRef.current.add(impressionKey);
            trackBannerImpression({ categoryType, animalType, siteId, locale });
        };

        const element = bannerRef.current;

        if (typeof IntersectionObserver === 'undefined' || !element) {
            trackImpression();
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        trackImpression();
                        observer.disconnect();
                    }
                });
            },
            { threshold: 0.2 },
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [
        banner?.desktopImage,
        categoryType,
        animalType,
        siteId,
        locale,
        isFlyoutOpen,
    ]);

    if (!banner?.desktopImage) {
        return null;
    }

    const { targetLink, linkAriaLabel, desktopImage, desktopImageAltText } =
        banner;

    const handleClick = () => {
        trackBannerClick({ categoryType, animalType, siteId, locale });
    };

    return (
        <a
            ref={bannerRef}
            href={targetLink}
            aria-label={linkAriaLabel ?? undefined}
            className={styles.bannerLink}
            data-zta="category-flyout-banner"
            onClick={handleClick}>
            <img
                src={desktopImage}
                alt={desktopImageAltText ?? ''}
                className={styles.bannerImage}
            />
        </a>
    );
}
